import React from "react";
import { ShoppingCart } from "lucide-react";
// import { getSupabaseProductImageUrl } from "../../../utils/imageUtils";
import type { Product } from "./Types";

interface QuickViewProps {
  product: Product;
  discountedPrice: number;
  imageUrl: string;
  onAddToCart?: () => void;
  onClose: () => void;
}

const QuickView: React.FC<QuickViewProps> = ({
  product,
  discountedPrice,
  imageUrl,
  onAddToCart,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl shadow-3xl p-0 max-w-lg w-full relative overflow-hidden">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-neutral-400 hover:text-white text-2xl font-bold focus:outline-none z-10"
          onClick={onClose}
          aria-label="Close quick view"
        >
          ×
        </button>
        {/* Image Section */}
        <div className="bg-neutral-800 flex items-center justify-center py-8 px-8 border-b border-neutral-800">
          <img
            src={imageUrl || product.image || "/assets/images/no-image.png"}
            alt={
              product.name ? `${product.name} product image` : "Product image"
            }
            className="object-contain w-48 h-48 rounded-xl shadow-lg"
            loading="lazy"
            decoding="async"
          />
        </div>
        {/* Details Section */}
        <div className="px-8 py-6">
          <div className="mb-2 text-lg font-bold text-white line-clamp-2">
            {product.name}
          </div>
          {product.brand_name && (
            <div className="text-xs text-neutral-400 mb-2 uppercase tracking-wide">
              {product.brand_name}
            </div>
          )}
          {/* Static 5-star rating */}
          <div className="flex gap-0.5 mb-3" aria-label="Rating: 5 out of 5">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                aria-hidden="true"
                className="w-4 h-4 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <title>Filled star</title>
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.174 9.393c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.966z" />
              </svg>
            ))}
          </div>
          {product.description && (
            <div className="mb-3 text-xs text-neutral-300">
              {product.description}
            </div>
          )}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl font-black text-primary">
              ${discountedPrice.toFixed(2)}
            </span>
            {discountedPrice !== product.price && product.price && (
              <span className="text-base text-neutral-500 line-through font-medium">
                ${product.price.toFixed(2)}
              </span>
            )}
          </div>
          <button
            className="w-full bg-linear-to-r from-primary-dark to-primary hover:from-primary-darker hover:to-primary-dark text-black py-3 rounded-xl font-bold text-base transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={() => {
              onAddToCart?.();
              onClose();
            }}
            aria-label="Add to cart from quick view"
          >
            <ShoppingCart className="w-5 h-5" />
            Add To Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickView;
